// Panel sabitleri — navigasyon, marka/departman listeleri ve renk eşlemeleri.
// Tasarımdaki (Call Agent Admin.dc.html) değerlerin birebir karşılığı.

import type { AgentState, AlertRuleType, Brand, BrandId, BreakEndReason, Severity, ShiftKind } from './types';

export type RouteKey =
  | 'canli-durum' | 'raporlar' | 'haftalik-rapor' | 'ajan-detayi' | 'liste-detayi' | 'bonus-kullanimi'
  | 'molalar' | 'uyarilar' | 'sms' | 'whatsapp' | 'winback' | 'butun-aramalar' | 'dark' | 'panel-sifreleri' | 'eklenti' | 'ayarlar';

export interface NavItem {
  key: RouteKey;
  label: string;
  icon: string;
  sub: string;
  adminOnly?: boolean;
  /** Kişi bazlı sayfa: yalnız `user.features[feature]` true olan oturum görür (rolden bağımsız —
   *  listede olmayan admin de görmez). Sunucu eşi: src/config.js winback.users + /api/winback 403. */
  feature?: 'winback';
}

/** Bir SÜPERVİZÖR neyi görür (sahibin listesi, görev #187/#188, 2026-10-09): Canlı Durum, Raporlar,
 *  Haftalık Rapor, Temsilci Detayı, Molalar, Uyarılar, SMS, WhatsApp, DARK + Panel Şifreleri (görev
 *  #216/#217, 2026-10-10) — KENDİ markalarında. adminOnly: Ayarlar + Eklenti; Winback kişi bazlı (`feature`). Haftalık Rapor TAM (görev #147/#148, 2026-09-30 — eski
 *  scorecard-only budaması emekli); veri kısıtı yalnız marka kapsamı (sunucu src/http/scope.js).
 *  Navigasyon admin'le aynı (nav gizleme `adminOnly` bayrağıyla, App.tsx `navAllowed`). */

export const NAV: NavItem[] = [
  { key: 'canli-durum', label: 'Canlı Durum', icon: 'sensors', sub: 'Temsilcilerin anlık arama durumları ve canlı akış' },
  { key: 'raporlar', label: 'Raporlar', icon: 'bar_chart', sub: 'Marka ve departman bazlı performans raporları' },
  { key: 'haftalik-rapor', label: 'Haftalık Rapor', icon: 'calendar_view_week', sub: 'Vegas + Prime haftalık çağrı ve yatırım karnesi' },
  { key: 'ajan-detayi', label: 'Temsilci Detayı', icon: 'support_agent', sub: 'Tekil temsilci metrikleri ve arama geçmişi' },
  // Görev #222 (+ kopyaları, Vegas #221, Hera #223…): Temsilci Detayı'nın ALTINDA. Veri haftalık rapordan →
  // Haftalık Rapor ile aynı marka kapısı (WEEKLY_REPORT_BRANDS, App.tsx weeklyVisible). Süpervizör de görür.
  { key: 'liste-detayi', label: 'Liste Detayı', icon: 'list_alt', sub: 'Listelerin günlük, haftalık ve aylık istatistikleri' },
  // Sahip 2026-10-27: bonus listesinde görüşülenlerin aramadan sonraki 48 saatte bonusu kullanma oranı. Kapı
  // BONUS_USAGE_BRANDS (Prime VIP dahil — veri haftalık rapordan DEĞİL, panelin kendi BackOffice okumasından).
  { key: 'bonus-kullanimi', label: 'Bonus Kullanımı', icon: 'redeem', sub: 'Bonus listesinde aramadan sonraki 48 saatte bonusu kullananlar' },
  { key: 'molalar', label: 'Molalar', icon: 'local_cafe', sub: 'Mola takibi, süreler ve kurallar' },
  { key: 'uyarilar', label: 'Uyarılar', icon: 'notifications', sub: 'Eşik aşımları ve sistem uyarıları' },
  { key: 'sms', label: 'SMS', icon: 'sms', sub: 'Gönderilen SMS kayıtları ve istatistikleri' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'chat', sub: 'WhatsApp / Telegram görüşme kayıtları ve istatistikleri' },
  { key: 'winback', label: 'Winback', icon: 'person_search', sub: 'Müşteri ID ile yatırım, bonus ve oyun analizi', feature: 'winback' },
  // Admin + süpervizör, KENDİ markalarında (görev #193/#194, 2026-10-10; sunucu src/api/customer-calls.js kapsamı).
  { key: 'butun-aramalar', label: 'Bütün Aramalar', icon: 'manage_search', sub: 'Müşteri ID ile o müşteriye yapılan bütün çağrılar' },
  { key: 'dark', label: 'DARK', icon: 'dark_mode', sub: 'DARK izleme modülü' },
  // Süpervizör de görür, KENDİ markalarıyla sınırlı (görev #216/#217, 2026-10-10; sunucu scope.js + panel-passwords.js).
  { key: 'panel-sifreleri', label: 'Panel Şifreleri', icon: 'lock_reset', sub: 'Temsilcilerin Call Agent paneline giriş şifreleri' },
  // Eklenti filo sayfası (tasarım C) — yalnız admin; çekmecesi komut gönderir (commands.js admin-only).
  { key: 'eklenti', label: 'Eklenti', icon: 'extension', sub: 'Call Agent eklentisi: sürümler ve dikkat isteyen paneller', adminOnly: true },
  { key: 'ayarlar', label: 'Ayarlar', icon: 'settings', sub: 'Sistem, kullanıcı ve erişim ayarları', adminOnly: true },
];

/** Marka çipi sırası — sunucu tarafındaki eşi src/api/live.js BRAND_ORDER
 *  (orası bir BEYAZ LİSTE filtresidir: orada olmayan marka telden hiç gelmez) ve
 *  src/http/scope.js ALL_BRAND_KEYS. Üçünü senkron tut. */
// BetBooker RETIRED 2026-09-09 — hidden, not deleted. Dropped from this catalog (twin of api.ts
// BRANDS) so no brand chip renders. BRAND_NAME + BRAND_TZ below deliberately KEEP betbooker so any
// historical BetBooker row (an agent's brand tag, a past record) still shows a proper label and the
// Accra tz rather than a raw key. Re-add this entry + the api.ts twin to un-hide.
export const ALL_BRANDS: Brand[] = [
  { id: 'vegas', name: 'Vegas' },
  { id: 'prime', name: 'Prime' },
  // Prime VIP hattı (2026-10-14) — Prime'ın yanında; sunucu eşi BRAND_ORDER / ALL_BRAND_KEYS.
  { id: 'primevip', name: 'Prime VIP' },
  { id: 'hera', name: 'Hera' },
];

export const BRAND_NAME: Record<string, string> = { vegas: 'Vegas', prime: 'Prime', primevip: 'Prime VIP', betbooker: 'BetBooker', hera: 'Hera' };

export const brandLabel = (id: BrandId | string) => BRAND_NAME[id] || id;

/** DARK (getircall PBX) yalnızca Vegas + Prime'da kullanılır — BetBooker DARK kullanmaz.
 *  Bu listede olmayan markalardan ibaret bir kapsam DARK içeriği (sayfa, karşılaştırma
 *  kartı, DARK uyarıları) GÖRMEZ. Sunucu tarafındaki eşi: src/engine/status-map.js. */
// primevip: Prime VIP hattının personeli aynı getircall santralinde (DARK) — sunucu eşi aynı.
export const DARK_BRANDS: BrandId[] = ['vegas', 'prime', 'primevip'];

/** Yalnızca DARK modülünün ürettiği uyarı kuralları (kapsamda DARK markası yoksa gizlenir). */
export const DARK_ALERT_RULES: AlertRuleType[] = ['dark_uyusmazligi', 'dark_eksik'];

/** Verilen marka kümesinde DARK kullanan en az bir marka var mı? */
export const hasDarkBrand = (ids: readonly BrandId[]) => ids.some((b) => DARK_BRANDS.includes(b));

/** Haftalık Rapor: Vegas + Prime (sahibin 2026-08-12 kararı) + Hera (2026-09-17, aynı TR
 *  çağrı dosyası düzeni; sunucu HERA_SHEET_ID + paylaşım gelene dek o yuvayı `unconfigured`
 *  hatasıyla döner — sayfa ayakta kalır). BetBooker'ın çağrı dosyasında bu sekmeler ve para
 *  sütunları yok. DARK_BRANDS'ten AYRI bir sabittir — biri değişince diğeri sessizce
 *  sürüklenmemeli. Sunucu tarafındaki eşi: src/engine/weekly-report.js
 *  WEEKLY_REPORT_BRANDS — senkron tut. */
export const WEEKLY_REPORT_BRANDS: BrandId[] = ['vegas', 'prime', 'hera'];

/** Verilen marka kümesinde haftalık raporu olan en az bir marka var mı? */
export const hasWeeklyReportBrand = (ids: readonly BrandId[]) => ids.some((b) => WEEKLY_REPORT_BRANDS.includes(b));

/** Canlı Durum "Bu hafta" şeridi (görev #195 Vegas / #196 Prime): yalnız bu iki marka istendi —
 *  Hera'yı da WEEKLY_REPORT_BRANDS'ten miras almasın (karne skorunda sahibin "Hera'dan kaldır"
 *  emsali). WEEKLY_REPORT_BRANDS'in alt kümesi olmak ZORUNDA (veri haftalık rapordan gelir). */
export const LIVE_WEEK_BRANDS: BrandId[] = ['vegas', 'prime'];

/** WhatsApp sayfası: paylaşılan WhatsApp dosyasında sekmesi olan markalar (sahip 2026-09-20,
 *  görev #104/#105). Sunucu eşi src/config.js `whatsapp.tabs` (WHATSAPP_SHEET_TABS) — senkron
 *  tut; kapsam dışı marka için sunucu zaten sıfır döner, bu sabit yalnız nav'ı gizler. */
export const WHATSAPP_BRANDS: BrandId[] = ['vegas', 'prime'];

/** Verilen marka kümesinde WhatsApp dosyası olan en az bir marka var mı? */
export const hasWhatsappBrand = (ids: readonly BrandId[]) => ids.some((b) => WHATSAPP_BRANDS.includes(b));

/** Winback sayfası: BackOffice hesabı yapılandırılmış markalar (sunucu src/config.js
 *  backoffice.brands — Vegas + Prime). Kapsam dışı marka için sunucu 409 `unconfigured` döner;
 *  bu sabit yalnız marka seçicisini kısıtlar. */
export const WINBACK_BRANDS: BrandId[] = ['vegas', 'prime'];

/** Call Agent panel sign-in (name + şifre) runs on the tr brands' proxies only — BetBooker has none.
 *  Server twin: src/api/panel-passwords.js PANEL_PASSWORD_BRANDS. */
export const PANEL_PASSWORD_BRANDS: BrandId[] = ['vegas', 'prime', 'primevip', 'hera'];
export const hasPanelPasswordBrand = (ids: readonly BrandId[]) => ids.some((b) => PANEL_PASSWORD_BRANDS.includes(b));

/** Bonus Kullanımı sayfasının markaları (sahip 2026-10-27). Server twin: src/engine/bonus-usage.js BONUS_USAGE_BRANDS. */
export const BONUS_USAGE_BRANDS: BrandId[] = ['vegas', 'prime', 'primevip', 'hera'];
export const hasBonusUsageBrand = (ids: readonly BrandId[]) => ids.some((b) => BONUS_USAGE_BRANDS.includes(b));

/** Vardiya planı (`Info` sekmesi) yalnızca BetBooker'da var — Vegas/Prime'ın böyle bir
 *  sayfası yok. Sunucu tarafındaki eşi: src/config.js schedule.brands. */
export const SCHEDULE_BRANDS: BrandId[] = ['betbooker'];

/** Yalnızca vardiya planından doğan uyarı kuralları (kapsamda planlı marka yoksa gizlenir). */
export const SCHEDULE_ALERT_RULES: AlertRuleType[] = [
  'plan_gelmedi', 'plan_disi_calisma', 'plan_eslesmeyen_ajan', 'plan_guncel_degil',
];

/** Verilen marka kümesinde vardiya planı olan en az bir marka var mı? */
export const hasScheduleBrand = (ids: readonly BrandId[]) => ids.some((b) => SCHEDULE_BRANDS.includes(b));

/** Artık HİÇBİR motorun üretmediği kurallar: kural tipi filtresinde gösterilmez (asla
 *  eşleşmeyecek bir seçenek bozuk filtre gibi okunur), ama RULE_TITLES'taki başlığı durur —
 *  migration 008'den sağ çıkmış eski bir satır yine de adıyla render edilsin diye.
 *  Sunucu tarafındaki eşi: src/engine/status-map.js RETIRED_ALERT_RULES. */
export const RETIRED_ALERT_RULES: AlertRuleType[] = ['plan_disi_calisma'];

/** Vardiya rozeti: [renk, arka plan, kenarlık, ikon]. */
export const SHIFT_META: Record<ShiftKind, [string, string, string, string]> = {
  morning: ['#3d8bff', 'rgba(61,139,255,.12)', 'rgba(61,139,255,.32)', 'light_mode'],
  evening: ['#a97cf0', 'rgba(169,124,240,.12)', 'rgba(169,124,240,.32)', 'dark_mode'],
  off: ['#6b7280', 'rgba(107,114,128,.14)', 'rgba(107,114,128,.32)', 'event_busy'],
  tl: ['#2fb8a8', 'rgba(47,184,168,.12)', 'rgba(47,184,168,.32)', 'supervisor_account'],
  fixed: ['#3d8bff', 'rgba(61,139,255,.12)', 'rgba(61,139,255,.32)', 'schedule'],
};

/** Gelen çağrı görevi rozeti — vardiya rozetinin YERİNE geçer (aynı dizi düzeni).
 *  Kendi rengi var: sabah/akşam vardiyasıyla aynı görünseydi "bu kişi neden hiç
 *  çağrı yapmıyor" sorusunun cevabı kartta görünmezdi. */
export const INBOUND_DUTY_META: Record<string, [string, string, string, string]> = {
  morning_inbound: ['#2fb8a8', 'rgba(47,184,168,.12)', 'rgba(47,184,168,.32)', 'call_received'],
  evening_inbound: ['#2fb8a8', 'rgba(47,184,168,.12)', 'rgba(47,184,168,.32)', 'call_received'],
};

/** Marka saat dilimi: Vegas/Prime/Hera = İstanbul, BetBooker = Akra (Gana, UTC+0).
 *  Sunucu tarafındaki eşi: src/engine/istanbul.js BRAND_TZ / tzOfBrands — senkron tut. */
export const BRAND_TZ: Record<BrandId, string> = {
  vegas: 'Europe/Istanbul',
  prime: 'Europe/Istanbul',
  betbooker: 'Africa/Accra',
  hera: 'Europe/Istanbul',
  primevip: 'Europe/Istanbul',
};

/** Seçili marka kümesinin saat dilimi: TÜMÜ Akra-dilimliyse Akra, aksi halde İstanbul. */
export const tzOfBrands = (ids: readonly BrandId[]): string => {
  const list = ids.filter(Boolean);
  return list.length && list.every((b) => BRAND_TZ[b] === 'Africa/Accra') ? 'Africa/Accra' : 'Europe/Istanbul';
};

/** Bir kart bu kadar dakikadır "müşteri işlemde" ise artık GÖRÜŞME değil, PARK EDİLMİŞ bir
 *  paneldir: müşteri açık bırakılıp gidilmiş. Eklentinin `AwaitingOutcome` durumu ikisini
 *  ayırt EDEMEZ (müşteri açık → sonuç girilene kadar sürer, kimse hatta olmasa da), bu yüzden
 *  ayrımı süre yapar — bu iş akışında gerçek bir görüşme dakikalarla ölçülür.
 *  Yalnızca GÖSTERİM eşiği (sunucu ham `handlingSince`'i verir); backend eşi yok. */
export const HANDLING_IDLE_AFTER_MIN = 20;

export const DEPARTMENTS = ['VIP', 'PREVIP', 'VEGAS CALL', 'PRIME CALL', 'BETBOOKER CALL', 'HERA CALL'];

// ── SMS (giden SMS paneli — migration 022) ────────────────────────────────────
/** Şablon id → [etiket, renk]. Eklentinin SMS_TEMPLATE_META'sının (call-agent-extension
 *  src/shared/constants.ts) admin tarafındaki eşi — 5 sabit id, SENKRON TUT. Etiket TR
 *  literal'dir; EN karşılığı i18n.ts'te. Bilinmeyen id ham gösterilir (sessizce gizlenmez). */
export const SMS_TEMPLATE_META: Record<string, [string, string]> = {
  guncel_link: ['Güncel Link', '#cdd6e6'],
  wp: ['WhatsApp', '#34d399'],
  tg: ['Telegram', '#4c8dff'],
  wp_tg: ['WA + TG', '#5eead4'],
  yatirim_iptal: ['Yatırım İptal', '#fbbf24'],
};
/** Bilinmeyen bir şablon slug'ını okunur hale getirir: `bonus_1000` → "Bonus 1000", `yeni_hat` →
 *  "Yeni Hat". Prime VIP hattının şablonları (2026-10-14) temsilci başına SMS sheet'inden gelir ve id'leri
 *  satır etiketinin slug'ıdır (davet, bonus_1000, bonus_2500, bonus, yeni_hat …); sahip yeni satırı
 *  sürüm çıkmadan ekler, yani her id'yi burada tek tek sayamayız. Slug biçiminde olmayan id ham kalır. */
export const humanizeSmsSlug = (id: string): string =>
  /^[a-z0-9_]+$/.test(id)
    ? id
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toLocaleUpperCase('tr') + w.slice(1))
        .join(' ')
    : id;
export const smsTemplateLabel = (id: string): string => SMS_TEMPLATE_META[id]?.[0] || (id ? humanizeSmsSlug(id) : 'Şablonsuz');
export const smsTemplateColor = (id: string): string => SMS_TEMPLATE_META[id]?.[1] || '#9aa4b2';

/** Günlük süzgecinin şablon seçenekleri: sabit 5 id (SMS_TEMPLATE_META sırası) + raporda görülen diğer
 *  id'ler (etikete göre sıralı) + seçili olan. Yalnız META'dan kurulsaydı VIP şablonları hiç seçilemezdi. */
export const smsTemplateOptionIds = (seen: Iterable<string>, selected = ''): string[] => {
  const meta = Object.keys(SMS_TEMPLATE_META);
  const extra = new Set<string>();
  for (const id of seen) if (id && !meta.includes(id)) extra.add(id);
  if (selected && !meta.includes(selected)) extra.add(selected);
  return meta.concat([...extra].sort((a, b) => smsTemplateLabel(a).localeCompare(smsTemplateLabel(b), 'tr')));
};

/** SMS sonuç → [etiket, renk] (Gönderildi / Başarısız). */
export const SMS_OUTCOME_META: Record<string, [string, string]> = {
  sent: ['Gönderildi', '#37c46b'],
  failed: ['Başarısız', '#ef5d5d'],
};
export const smsOutcomeLabel = (o: string): string => SMS_OUTCOME_META[o]?.[0] || o;
export const smsOutcomeColor = (o: string): string => SMS_OUTCOME_META[o]?.[1] || '#9aa4b2';

/** Başarısızlık sebebi (proxy'nin gerçek gateway sebebi) → TR etiket. Bilinmeyen sebep ham. */
export const SMS_REASON_LABELS: Record<string, string> = {
  rejected: 'Reddedildi',
  unreachable: 'Ulaşılamadı',
  send_disabled: 'Gönderim kapalı',
  not_configured: 'Yapılandırılmadı',
  invalid_phone: 'Geçersiz numara',
  empty_message: 'Boş mesaj',
  message_too_long: 'Mesaj çok uzun',
};
export const smsReasonLabel = (r: string): string => SMS_REASON_LABELS[r] || r || '';

/** Temsilci durum noktası + rozet renkleri (Canlı Durum). */
export const STATE_META: Record<AgentState, { label: string; color: string }> = {
  working: { label: 'Çalışıyor', color: '#37c46b' },
  break: { label: 'Molada', color: '#e0a23c' },
  offline: { label: 'Çevrimdışı', color: '#6b7280' },
};

/** Uyarı önem dereceleri: [renk, arka plan, kenarlık, etiket]. */
export const SEV_META: Record<Severity, [string, string, string, string]> = {
  info: ['#3d8bff', 'rgba(61,139,255,.14)', 'rgba(61,139,255,.4)', 'Bilgi'],
  warning: ['#e0a23c', 'rgba(224,162,60,.14)', 'rgba(224,162,60,.4)', 'Uyarı'],
  critical: ['#ef5d5d', 'rgba(239,93,93,.14)', 'rgba(239,93,93,.4)', 'Kritik'],
};

/** Mola bitiş nedeni rozetleri: [renk, arka plan, kenarlık]. */
export const REASON_META: Record<BreakEndReason, [string, string, string]> = {
  Temsilci: ['#9aa4b2', 'rgba(154,164,178,.12)', 'rgba(154,164,178,.28)'],
  'Zaman aşımı': ['#ef6d6d', 'rgba(239,93,93,.12)', 'rgba(239,93,93,.3)'],
  Admin: ['#3d8bff', 'rgba(61,139,255,.12)', 'rgba(61,139,255,.35)'],
  'Gece yarısı': ['#e0a23c', 'rgba(224,162,60,.12)', 'rgba(224,162,60,.3)'],
};

/** Kural tipi → başlık, VARSAYILAN eşiklerle. Bu tablo kural tipi kümesinin ve sıranın
 *  kaynağıdır, ama GÖRÜNEN metnin son sözü DEĞİLDİR: eşik alıntılayan başlıklar
 *  (`uzun_bosluk`) `config_kv.thresholds` ile değişir ve sunucudan gelir
 *  (`getAlertRuleTitles()` → GET /api/alert-rules). Burası yalnızca istek dönmeden
 *  önceki/başarısız olduğundaki yedektir. Sunucu eşi: src/engine/status-map.js
 *  ALERT_RULES + alertRuleTitles.
 *
 *  Bir başlığı DEĞİŞTİRİRSEN i18n.ts'teki karşılığını da güncelle (CLAUDE.md §2);
 *  `uzun_bosluk` için o karşılık artık düz sözlük satırı değil, sayıyı yakalayan bir
 *  regex — yoksa EN modunda 15 dk'lık başlık Türkçe kalır. */
export const RULE_TITLES: Record<AlertRuleType, string> = {
  mola_oncesi_bosta: 'Mola öncesi boşta kalma',
  mola_asimi: 'Mola hakkı aşıldı',
  dark_uyusmazligi: 'DARK çağrı sayısı uyuşmazlığı (>20)',
  dark_eksik: 'DARK raporu yüklenmedi',
  uzun_bosluk: 'Uzun boşluk (>30 dk)', // eşik alıntılar — canlısı sunucudan gelir
  bilinmeyen_ajan: 'Eşleşmeyen temsilci adı',
  sync_donduruldu: 'Sheet senkronu donduruldu',
  mola_otomatik_kapandi: 'Mola otomatik kapatıldı',
  bilinmeyen_durum: 'Bilinmeyen durum değeri',
  plan_gelmedi: 'Vardiyaya gelmedi',
  plan_disi_calisma: 'Plan dışı çalışma',
  plan_eslesmeyen_ajan: 'Vardiya planında eşleşmeyen isim',
  plan_guncel_degil: 'Vardiya planı okunamadı',
  saat_sapmasi: 'Bilgisayar saati sapması',
  sonuc_kaybi: 'Çağrı sonucu kaybı',
  dahili_uyusmazligi: 'Dahili (KOD) uyuşmazlığı',
  cagri_hedefi_alti: 'Günlük çağrı hedefinin altında',
  dusuk_yanit_orani: 'Düşük yanıtlanma oranı',
};

/** Mola verme modalindeki sebep seçenekleri. */
export const GRANT_REASONS = ['Yoğunluk', 'Sağlık molası', 'Yönetici onayı', 'Teknik sorun', 'Diğer'];

/** localStorage anahtarları (oturum kalıcılığı). */
export const LS = {
  session: 'caa.session',
  brands: 'caa.brands',
  route: 'caa.route',
  lang: 'caa.lang',
  nav: 'caa.nav', // '1' = kenar çubuğu daraltılmış (tercih — çıkışta silinmez)
  theme: 'caa.theme', // 'light' | 'dark' (tercih — çıkışta silinmez; index.html satır içi betiği de okur)
} as const;
